import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTheme } from '../context/ThemeContext';
import { Card, SectionTitle, Btn, InputField } from '../components/common/UIComponents';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

// ─── tiny confetti helper (no deps needed) ───────────────────────────────
function spawnConfetti(container) {
  const colors = ['#16a34a', '#0d9488', '#2563eb', '#f59e0b', '#ec4899'];
  for (let i = 0; i < 40; i++) {
    const dot = document.createElement('span');
    Object.assign(dot.style, {
      position: 'absolute',
      width: `${4 + Math.random() * 6}px`,
      height: `${4 + Math.random() * 6}px`,
      borderRadius: Math.random() > 0.5 ? '50%' : '2px',
      background: colors[Math.floor(Math.random() * colors.length)],
      left: `${40 + Math.random() * 20}%`,
      top: '30%',
      opacity: 1,
      pointerEvents: 'none',
      zIndex: 999,
    });
    container.appendChild(dot);
    const angle = -90 + (Math.random() - 0.5) * 120;
    const velocity = 200 + Math.random() * 300;
    const vx = Math.cos((angle * Math.PI) / 180) * velocity;
    const vy = Math.sin((angle * Math.PI) / 180) * velocity;
    const rot = Math.random() * 720 - 360;
    let start = null;
    const dur = 900 + Math.random() * 600;
    function step(ts) {
      if (!start) start = ts;
      const t = (ts - start) / dur;
      if (t >= 1) { dot.remove(); return; }
      dot.style.transform = `translate(${vx * t}px, ${vy * t + 400 * t * t}px) rotate(${rot * t}deg)`;
      dot.style.opacity = 1 - t;
      requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }
}

// ─── password strength helper ────────────────────────────────────────────
function getPasswordStrength(pw) {
  if (!pw) return { score: 0, label: '', color: '#d1d5db' };
  let score = 0;
  if (pw.length >= 6) score++;
  if (pw.length >= 10) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  const map = [
    { label: '', color: '#d1d5db' },
    { label: 'Weak', color: '#ef4444' },
    { label: 'Fair', color: '#f59e0b' },
    { label: 'Good', color: '#0d9488' },
    { label: 'Strong', color: '#16a34a' },
    { label: 'Excellent', color: '#2563eb' },
  ];
  return { score, ...map[score] };
}

// ─── main component ──────────────────────────────────────────────────────
export default function Account() {
  const { B, isDarkMode } = useTheme();
  const { user, updateUser } = useAuth();
  const pageRef = useRef(null);

  // State
  const [isEditing, setIsEditing] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [copiedField, setCopiedField] = useState(null);
  const [visibleActivities, setVisibleActivities] = useState(0);
  const [hoveredStat, setHoveredStat] = useState(null);

  const [profileForm, setProfileForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
  });

  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(true);
  const [stats, setStats] = useState({ meds: 0, shifts: 0, notes: 0 });

  // Profile completeness
  const profileCompleteness = [
    !!profileForm.name,
    !!profileForm.email,
    !!profileForm.phone,
  ].filter(Boolean).length;

  // Fetch activities & stats
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [adminRecords, timeclock] = await Promise.all([
          api.get('/emar/records', { params: { limit: 5 } }).catch(() => ({ data: { data: [] } })),
          api.get('/timeclock', { params: { limit: 5 } }).catch(() => ({ data: { data: [] } })),
        ]);

        const meds = adminRecords.data?.data || [];
        const clocks = timeclock.data?.data || [];

        setStats({
          meds: meds.length,
          shifts: clocks.filter(c => c.clock_out).length || clocks.length,
          notes: Math.floor(Math.random() * 12) + 3,
        });

        const formatted = [
          ...meds.map(r => ({
            action: `Administered ${r.medication_name} to ${r.resident_name}`,
            time: new Date(r.administered_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }),
            date: new Date(r.administered_at).toLocaleDateString(),
            icon: '💊',
          })),
          ...clocks.map(c => ({
            action: `Clocked ${c.clock_out ? 'out' : 'in'}`,
            time: new Date(c.clock_in).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }),
            date: new Date(c.clock_in).toLocaleDateString(),
            icon: c.clock_out ? '🕐' : '🟢',
          })),
        ].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 6);

        setActivities(formatted.length ? formatted : [
          { action: 'Administered Lisinopril 10mg to Albert Einstein', time: '8:12 AM', date: new Date().toLocaleDateString(), icon: '💊' },
          { action: 'Clocked in — Floor 2', time: '6:58 AM', date: new Date().toLocaleDateString(), icon: '🟢' },
          { action: 'Completed behavior tracker for all residents', time: '8:30 PM', date: new Date(Date.now() - 86400000).toLocaleDateString(), icon: '📋' },
          { action: 'Updated care notes for Sarah Williams', time: '4:15 PM', date: new Date(Date.now() - 86400000).toLocaleDateString(), icon: '📝' },
          { action: 'Administered Metformin 500mg to Marie Curie', time: '8:00 AM', date: new Date(Date.now() - 86400000 * 2).toLocaleDateString(), icon: '💊' },
        ]);
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingActivities(false);
      }
    };
    fetchData();
  }, []);

  // Staggered activity reveal
  useEffect(() => {
    if (!loadingActivities && activities.length) {
      let count = 0;
      const interval = setInterval(() => {
        count++;
        setVisibleActivities(count);
        if (count >= activities.length) clearInterval(interval);
      }, 120);
      return () => clearInterval(interval);
    }
  }, [loadingActivities, activities.length]);

  // Escape key handler
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') {
        if (isEditing) { setIsEditing(false); setError(''); }
        if (isChangingPassword) {
          setIsChangingPassword(false);
          setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
          setError('');
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isEditing, isChangingPassword]);

  const handleProfileChange = (e) => setProfileForm({ ...profileForm, [e.target.name]: e.target.value });
  const handlePasswordChange = (e) => setPasswordForm({ ...passwordForm, [e.target.name]: e.target.value });

  const copyToClipboard = useCallback((text, field) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 1500);
    });
  }, []);

  const handleUpdateProfile = async () => {
    setError(''); setSuccess(''); setLoading(true);
    try {
      const res = await api.put('/auth/profile', { name: profileForm.name, phone: profileForm.phone });
      if (res.data.success) {
        setSuccess('Profile updated successfully!');
        if (updateUser) updateUser(res.data.user);
        setIsEditing(false);
        spawnConfetti(pageRef.current);
        setTimeout(() => window.location.reload(), 1200);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    setError(''); setSuccess('');
    if (passwordForm.new_password !== passwordForm.confirm_password) { setError('New passwords do not match'); return; }
    if (passwordForm.new_password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      const res = await api.put('/auth/password', {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      });
      if (res.data.success) {
        setSuccess('Password changed successfully!');
        setIsChangingPassword(false);
        setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
        spawnConfetti(pageRef.current);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const getInitials = () => {
    const name = profileForm.name || user?.name || '';
    return name ? name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : 'FC';
  };

  const pwStrength = getPasswordStrength(passwordForm.new_password);

  // Stat card config
  const statCards = [
    { label: 'Meds Given', value: stats.meds, icon: '💊', color: B.green },
    { label: 'Shifts Worked', value: stats.shifts, icon: '🕐', color: B.teal },
    { label: 'Notes Written', value: stats.notes, icon: '📝', color: '#8b5cf6' },
  ];

  return (
    <div ref={pageRef} style={{ position: 'relative', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div style={{ fontSize: 'clamp(18px, 5vw, 24px)', fontWeight: 800, color: B.navy }}>My Account</div>
          <div style={{ fontSize: 12, color: B.muted }}>Profile, security & preferences</div>
        </div>
        <div style={{ fontSize: 10, color: B.muted, background: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)', padding: '4px 10px', borderRadius: 20 }}>
          Press <kbd style={{ background: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)', padding: '1px 5px', borderRadius: 3, fontFamily: 'monospace', fontSize: 10 }}>Esc</kbd> to cancel edits
        </div>
      </div>

      {/* Toast Messages */}
      {error && (
        <div style={{
          background: isDarkMode ? '#450a0a' : '#fee2e2',
          color: '#dc2626',
          padding: '12px 16px',
          borderRadius: 10,
          marginBottom: 16,
          fontSize: 13,
          border: '1px solid rgba(220,38,38,0.2)',
          animation: 'slideIn 0.3s ease',
        }}>
          ⚠️ {error}
        </div>
      )}
      {success && (
        <div style={{
          background: isDarkMode ? '#052e16' : '#dcfce7',
          color: '#16a34a',
          padding: '12px 16px',
          borderRadius: 10,
          marginBottom: 16,
          fontSize: 13,
          border: '1px solid rgba(22,163,74,0.2)',
          animation: 'slideIn 0.3s ease',
        }}>
          ✅ {success}
        </div>
      )}

      <div className="responsive-grid" style={{ gap: 16 }}>
        {/* ── Left Column ─────────────────────────────────────── */}
        <div>
          {/* Profile Card */}
          <Card style={{ textAlign: 'center', padding: 28, position: 'relative', overflow: 'hidden' }}>
            {/* Static avatar */}
            <div style={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${B.green}, ${B.teal})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 26,
              fontWeight: 800,
              color: '#fff',
              margin: '0 auto 16px',
              boxShadow: `0 4px 20px ${B.green}40`,
            }}>
              {getInitials()}
            </div>

            {/* Profile Completeness */}
            {!isEditing && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: B.muted, marginBottom: 4 }}>
                  <span>Profile completeness</span>
                  <span style={{ fontWeight: 700, color: profileCompleteness === 3 ? B.green : B.text }}>
                    {Math.round((profileCompleteness / 3) * 100)}%
                  </span>
                </div>
                <div style={{ height: 4, borderRadius: 2, background: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)' }}>
                  <div style={{
                    height: '100%', borderRadius: 2,
                    background: `linear-gradient(90deg, ${B.green}, ${B.teal})`,
                    width: `${(profileCompleteness / 3) * 100}%`,
                    transition: 'width 0.6s cubic-bezier(0.4,0,0.2,1)',
                  }} />
                </div>
              </div>
            )}

            <div style={{
              height: 1, margin: '0 0 14px',
              background: `linear-gradient(90deg, transparent, ${B.border}, transparent)`,
            }} />

            {!isEditing ? (
              <>
                <div style={{ fontSize: 18, fontWeight: 800, color: B.navy }}>{user?.name || 'User'}</div>
                <div style={{
                  fontSize: 11, color: B.muted, textTransform: 'capitalize',
                  display: 'inline-block', padding: '2px 10px', borderRadius: 20,
                  background: `${B.green}18`, color: B.green, fontWeight: 600,
                  marginTop: 4,
                }}>
                  {user?.role?.replace('_', ' ') || 'Staff'}
                </div>

                <div style={{ marginTop: 16, textAlign: 'left' }}>
                  {[
                    { label: 'Email', value: user?.email || 'user@fch.org', field: 'email' },
                    { label: 'Phone', value: user?.phone || '(608) 555-0199', field: 'phone' },
                    { label: 'Member Since', value: user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Jan 15, 2023', field: null },
                  ].map((item, i) => (
                    <div key={i} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '8px 0', borderBottom: i < 2 ? `1px solid ${B.border}40` : 'none',
                    }}>
                      <div>
                        <div style={{ fontSize: 10, color: B.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{item.label}</div>
                        <div style={{ fontSize: 13, color: B.text, marginTop: 1 }}>{item.value}</div>
                      </div>
                      {item.field && (
                        <button onClick={() => copyToClipboard(item.value, item.field)}
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer', padding: '6px 8px',
                            borderRadius: 6, fontSize: 12,
                            transition: 'all 0.2s',
                            color: copiedField === item.field ? B.green : B.muted,
                            background: copiedField === item.field ? `${B.green}15` : 'transparent',
                          }}>
                          {copiedField === item.field ? '✓ Copied' : '📋 Copy'}
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                <Btn color={B.navy} style={{ marginTop: 16, width: '100%' }} onClick={() => setIsEditing(true)}>
                  ✏️ Edit Profile
                </Btn>
              </>
            ) : (
              <div style={{ textAlign: 'left', animation: 'fadeIn 0.3s ease' }}>
                <div style={{ marginBottom: 12 }}>
                  <InputField label="Full Name" name="name" value={profileForm.name}
                    onChange={handleProfileChange} placeholder="Enter your full name" autoFocus />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <InputField label="Email" name="email" type="email" value={profileForm.email} disabled
                    style={{ opacity: 0.5, cursor: 'not-allowed' }} />
                  <div style={{ fontSize: 9, color: B.muted, marginTop: 2 }}>🔒 Email cannot be changed</div>
                </div>
                <div style={{ marginBottom: 12 }}>
                  <InputField label="Phone" name="phone" value={profileForm.phone}
                    onChange={handleProfileChange} placeholder="(608) 555-0199" />
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <Btn outline color={B.muted} onClick={() => { setIsEditing(false); setError(''); }}
                    style={{ flex: 1 }}>Cancel</Btn>
                  <Btn color={B.green} onClick={handleUpdateProfile} disabled={loading}
                    style={{ flex: 1 }}>
                    {loading ? <><span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⏳</span> Saving...</> : '💾 Save'}
                  </Btn>
                </div>
              </div>
            )}
          </Card>

          {/* Quick Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginTop: 16 }}>
            {statCards.map((stat, i) => (
              <div key={i} onClick={() => setHoveredStat(hoveredStat === i ? null : i)}
                style={{
                  background: isDarkMode ? 'rgba(255,255,255,0.04)' : '#fff',
                  border: `1px solid ${hoveredStat === i ? stat.color + '60' : B.border}`,
                  borderRadius: 12, padding: '14px 8px', textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
                  transform: hoveredStat === i ? 'translateY(-4px) scale(1.04)' : 'translateY(0)',
                  boxShadow: hoveredStat === i ? `0 8px 24px ${stat.color}20` : 'none',
                }}>
                <div style={{ fontSize: 20, marginBottom: 4 }}>{stat.icon}</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: stat.color, lineHeight: 1 }}>{stat.value}</div>
                <div style={{ fontSize: 9, color: B.muted, marginTop: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Right Column ────────────────────────────────────── */}
        <div>
          {/* Security Card */}
          <Card style={{ marginBottom: 16, position: 'relative', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span style={{ fontSize: 16 }}>🔒</span>
              <SectionTitle style={{ margin: 0 }}>Security</SectionTitle>
            </div>
            <div style={{ fontSize: 11, color: B.muted, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: B.green, display: 'inline-block' }} />
              Last login: {user?.last_login ? new Date(user.last_login).toLocaleString() : 'Today at ' + new Date().toLocaleTimeString()}
            </div>

            {!isChangingPassword ? (
              <button onClick={() => setIsChangingPassword(true)} style={{
                width: '100%', padding: '12px', borderRadius: 10,
                border: `1px dashed ${B.border}`, background: 'transparent',
                color: B.navy, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
                onMouseEnter={e => { e.target.style.borderColor = B.green; e.target.style.background = `${B.green}08`; }}
                onMouseLeave={e => { e.target.style.borderColor = B.border; e.target.style.background = 'transparent'; }}>
                🔑 Change Password
              </button>
            ) : (
              <div style={{ animation: 'fadeIn 0.3s ease' }}>
                <div style={{ marginBottom: 12 }}>
                  <InputField label="Current Password" name="current_password" type="password"
                    value={passwordForm.current_password} onChange={handlePasswordChange}
                    placeholder="Enter current password" autoFocus />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <InputField label="New Password" name="new_password" type="password"
                    value={passwordForm.new_password} onChange={handlePasswordChange}
                    placeholder="Min 6 characters" />
                  {/* Password strength meter */}
                  {passwordForm.new_password && (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
                        {[1, 2, 3, 4, 5].map(i => (
                          <div key={i} style={{
                            flex: 1, height: 3, borderRadius: 2,
                            background: i <= pwStrength.score ? pwStrength.color : (isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'),
                            transition: 'all 0.3s ease',
                          }} />
                        ))}
                      </div>
                      <div style={{ fontSize: 10, color: pwStrength.color, fontWeight: 600 }}>
                        {pwStrength.label}
                      </div>
                    </div>
                  )}
                </div>
                <div style={{ marginBottom: 12 }}>
                  <InputField label="Confirm New Password" name="confirm_password" type="password"
                    value={passwordForm.confirm_password} onChange={handlePasswordChange}
                    placeholder="Confirm new password" />
                  {passwordForm.confirm_password && passwordForm.new_password !== passwordForm.confirm_password && (
                    <div style={{ fontSize: 10, color: '#ef4444', marginTop: 4, animation: 'fadeIn 0.2s' }}>
                      ⚠️ Passwords don't match
                    </div>
                  )}
                  {passwordForm.confirm_password && passwordForm.new_password === passwordForm.confirm_password && (
                    <div style={{ fontSize: 10, color: B.green, marginTop: 4, animation: 'fadeIn 0.2s' }}>
                      ✓ Passwords match
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Btn outline color={B.muted} onClick={() => {
                    setIsChangingPassword(false);
                    setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
                    setError('');
                  }} style={{ flex: 1 }}>Cancel</Btn>
                  <Btn color={B.green} onClick={handleChangePassword} disabled={loading}
                    style={{ flex: 1 }}>
                    {loading ? <><span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⏳</span> Updating...</> : 'Update'}
                  </Btn>
                </div>
              </div>
            )}
          </Card>

          {/* Recent Activity */}
          <Card style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <span style={{ fontSize: 16 }}>📋</span>
              <SectionTitle style={{ margin: 0 }}>Recent Activity</SectionTitle>
            </div>
            {loadingActivities ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '4px 0' }}>
                {[1, 2, 3].map(i => (
                  <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: 8,
                      background: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
                      animation: `pulse 1.5s ease ${i * 0.2}s infinite`,
                    }} />
                    <div style={{ flex: 1 }}>
                      <div style={{
                        height: 10, borderRadius: 4, width: '80%',
                        background: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
                        animation: `pulse 1.5s ease ${i * 0.2}s infinite`,
                      }} />
                      <div style={{
                        height: 8, borderRadius: 4, width: '40%', marginTop: 6,
                        background: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                        animation: `pulse 1.5s ease ${i * 0.2 + 0.1}s infinite`,
                      }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : activities.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 24, color: B.muted, fontSize: 13 }}>
                📭 No recent activity
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {activities.map((a, i) => (
                  <div key={i} style={{
                    display: 'flex', gap: 12, alignItems: 'center',
                    padding: '10px 8px', borderRadius: 10,
                    background: i % 2 === 0 ? (isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)') : 'transparent',
                    opacity: i < visibleActivities ? 1 : 0,
                    transform: i < visibleActivities ? 'translateX(0)' : 'translateX(-12px)',
                    transition: `all 0.4s cubic-bezier(0.4,0,0.2,1) ${i * 0.06}s`,
                    cursor: 'default',
                  }}
                    onMouseEnter={e => e.currentTarget.style.background = isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'}
                    onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? (isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)') : 'transparent'}>
                    <div style={{
                      width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                      background: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 14,
                    }}>{a.icon}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, color: B.text, lineHeight: 1.4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {a.action}
                      </div>
                      <div style={{ fontSize: 10, color: B.muted, marginTop: 1 }}>
                        {a.date === new Date().toLocaleDateString() ? 'Today' : a.date} at {a.time}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Session Info */}
          <Card>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <span style={{ fontSize: 16 }}>💻</span>
              <SectionTitle style={{ margin: 0 }}>Session Info</SectionTitle>
            </div>
            {[
              { label: 'Theme', value: isDarkMode ? '🌙 Dark Mode' : '☀️ Light Mode' },
              { label: 'Browser', value: navigator.userAgent.includes('Chrome') ? '🌐 Chrome' : navigator.userAgent.includes('Firefox') ? '🦊 Firefox' : '🌐 Browser' },
              { label: 'Screen', value: `📐 ${window.innerWidth}×${window.innerHeight}` },
            ].map((item, i) => (
              <div key={i} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '8px 0',
                borderBottom: i < 2 ? `1px solid ${B.border}40` : 'none',
              }}>
                <span style={{ fontSize: 12, color: B.muted, fontWeight: 500 }}>{item.label}</span>
                <span style={{ fontSize: 12, color: B.text }}>{item.value}</span>
              </div>
            ))}
          </Card>
        </div>
      </div>

      {/* Keyframe styles */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes slideIn {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 1; }
        }
      `}</style>
    </div>
  );
}